'use client';

import { useRouter, usePathname } from 'next/navigation';
import { useEffect, useState, ReactNode, Fragment } from 'react';
import { routes } from '@lib/constants/routes-constant';
import { getFeatureFlagState } from '@lib/data';
import { featureFlags } from '@lib/constants/network-constants';
import { IS_BROWSER } from '@lib/constants';
import { deleteAllCookies } from '@lib/util/cookie';
import { Transition, Dialog } from '@headlessui/react';
import { ButtonPrimary } from '@modules/common/components/form/buttons/button-primary';
import { useStore } from '@lib/context/store-context';
import { getLogger } from '@lib/util/logging-util';

export function MaintenanceProvider({ children }: { children: ReactNode }) {
  const logger = getLogger();
  const router = useRouter();
  const pathname = usePathname();
  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [appVersion, setAppVersion] = useState<number>();
  const [appFeatureVersion, setAppFeatureVersion] = useState<number>();
  const { setLoading } = useStore();


  useEffect(() => {
    fetchMaintenanceState();
    setIsVisible(!document.hidden);
    getGCMVersion();

    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const startInterval = () => {
      if (!intervalId) {
        fetchMaintenanceState(); // Initial fetch
        intervalId = setInterval(fetchMaintenanceState, 30000);
      }
    };

    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    if (isVisible) {
      startInterval();
    } else {
      stopInterval();
    }

    return () => stopInterval(); // Cleanup on unmount
  }, [isVisible]);

  const fetchMaintenanceState = async () => {
    try {
      const mode = await getFeatureFlagState(featureFlags.MAINTENANCE_MODE);
      setMaintenanceMode(mode);
    } catch (error) {
      logger.error('Failed to fetch maintenance mode:', error);
    }
  };

  const getGCMVersion = async () => {
    try {
      const appVersion = await getFeatureFlagState(featureFlags.GCM_VERSION, 20000, true);
      if (appVersion) {
        setAppVersion(appVersion?.value ?? 0);
      }
    } catch (error) {
      logger.error('Failed to fetch GCM version:', error);
    }
  };
  const getGCMVersionFeatureChange = async () => {
    try {
      const appFeatureVersionRes = await getFeatureFlagState(featureFlags.VERSION_OF_FEATURE_FLAG_CHANGED, 20000, true);
      if (appFeatureVersionRes) {
        setAppFeatureVersion(appFeatureVersionRes?.value);
      }
    } catch (error) {
      logger.error('Failed to fetch GCM feature version:', error);
    }
  };

  const getIsClearCartCache = async () => {
    try {
      return !!(await getFeatureFlagState(featureFlags.CLEAR_CART_CACHE));
    } catch (error) {
      logger.error(`Failed to get ${featureFlags.CLEAR_CART_CACHE}:`, error);
    }
  };

  const handleClearCache = async () => {
    logger.info('Clearing cache');
    setLoading(true);
    const cartId = localStorage.getItem('medusa_cart_id');
    deleteAllCookies();
    localStorage.clear();
    sessionStorage.clear();
    const isNeedClearCart = await getIsClearCartCache();
    if (cartId && !isNeedClearCart) {
      localStorage.setItem('medusa_cart_id', cartId);
    }
    localStorage.setItem('app_version', JSON.stringify(appVersion));
    if (appFeatureVersion)
      localStorage.setItem('app_feature_version', JSON.stringify(appFeatureVersion));
    setLoading(false);
    return window.location.reload();
  };

  useEffect(() => {
    const checkVersion = () => {
      let appVersionLocal = localStorage.getItem('app_version');
      if (appVersionLocal === 'undefined') {
        appVersionLocal = '-1';
        localStorage.removeItem('app_version');
      }
      if (!appVersion) return;
      if (appVersionLocal) {
        let appVersionString;
        try {
          // Try to parse as JSON (new format)
          appVersionString = JSON.parse(appVersionLocal);
        } catch {
          // If parsing fails, use the raw value (old format)
          appVersionString = appVersionLocal;
        }
        if (Number(appVersion) != Number(appVersionString)) {
          handleClearCache();
        }
      }
      localStorage.setItem('app_version', JSON.stringify(appVersion));
    };
    const checkFeatureVersion = () => {
      const appFeatureVersionLocal = localStorage.getItem('app_feature_version');
      if (!appFeatureVersion) return;
      if (appFeatureVersionLocal) {
        let appVersionString;
        try {
          // Try to parse as JSON (new format)
          appVersionString = JSON.parse(appFeatureVersionLocal);
        } catch {
          // If parsing fails, use the raw value (old format)
          appVersionString = appFeatureVersionLocal;
        }
        if (Number(appFeatureVersion) != Number(appVersionString)) {
          handleClearCache();
        }
      } else {
        if (appFeatureVersion)
          localStorage.setItem('app_feature_version', JSON.stringify(appFeatureVersion));
      }
    };
    checkVersion();
    checkFeatureVersion();
  }, [appVersion, appFeatureVersion]);

  // Consolidated redirect logic into a single useEffect
  useEffect(() => {
    if (IS_BROWSER) {
      if (maintenanceMode && pathname !== routes.temporarily_unavailable) {
        router.push(routes.temporarily_unavailable);
      } else if (
        !maintenanceMode &&
        pathname === routes.temporarily_unavailable
      ) {
        router.push(routes.home);
      }
    }
    getGCMVersion();
    getGCMVersionFeatureChange();
  }, [maintenanceMode, pathname]);

  return <>
    {children}
    <>
      <Transition appear show={false} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => {
        }}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black opacity-50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white py-6 px-5 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-semibold leading-6 text-black px-6 my-2"
                  >
                    Time for a Quick Refresh
                  </Dialog.Title>
                  <div className="px-6">
                    <p className="text-base">A new version of the Gift Card Market is available! Just click 'Update Now'
                      to refresh and get the latest version.</p>
                    <div className="flex mt-4">
                      <ButtonPrimary
                        size="medium"
                        variant="active"
                        type="button"
                        onClick={() => handleClearCache()}
                        className="w-full focus:outline-none"
                      >
                        Update Now
                      </ButtonPrimary>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  </>;
}
